import React, { useState } from 'react';
import { Stack, IconButton, TextField, Typography, LinearProgress } from '@mui/material';
import { Delete, Edit, Check } from '@mui/icons-material';
import { orderDocumentsName } from '../ReferralDocuments';

export default function UploadedFilesInline({ files = [], onDelete, onRename, showProgress = false }) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!files || files.length === 0) return null;

  const saveNewName = (fileObj) => {
    if (onRename) {
      onRename(fileObj, editValue);
    }

    setEditingId(null);
    setEditValue('');
  };

  const getExtensionFromUrl = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      const pathParam = u.searchParams.get('path');
      if (!pathParam) return '';
      const decoded = decodeURIComponent(pathParam);
      const lastPart = decoded.split('/').pop() || '';
      const dotIndex = lastPart.lastIndexOf('.');
      return dotIndex !== -1 ? lastPart.substring(dotIndex) : '';
    } catch (e) {
      return '';
    }
  };

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textAlign: 'left' }}>
        Uploaded files
      </Typography>

      <Stack spacing={1}>
        {files.map((fileObj) => {
          const extension = getExtensionFromUrl(fileObj.url);
          const nameOnly = (fileObj.displayName || '').replace(extension, '');
          const isEditable = fileObj?.name?.toLowerCase() === 'misc' || fileObj?.name?.toLowerCase() === orderDocumentsName;

          return (
            <Stack
              key={fileObj.id || fileObj.name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 3,
                px: 2,
                py: 1,
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',

                '&:hover': {
                  backgroundColor: '#f0f9ff', 
                  borderColor: '#d1d5db',
                },

                '&:focus-visible': {
                  backgroundColor: '#f3f4f6',
                  outline: '2px solid #2563eb',
                  outlineOffset: '2px',
                },

                '&:active': {
                  backgroundColor: '#f3f4f6',
                },
              }}
              onClick={() => window.open(fileObj.url, '_blank')}
            >
              {editingId === fileObj.id ? (
                <TextField
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  sx={{ flex: 1, mr: 1 }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <Stack direction="row" spacing={0.3} sx={{ flex: 1, alignItems: 'center', overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ flexShrink: 0, fontWeight: 500 }}>
                    {nameOnly}{extension}
                  </Typography>
                  {showProgress && fileObj.progress != null && fileObj.status !== 'done' && (
                    <LinearProgress
                      variant="determinate"
                      value={fileObj.progress}
                      sx={{ mt: 0.5, height: 4, borderRadius: 1, flex: 1 }}
                    />
                  )}
                </Stack>
              )}

              <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                {editingId === fileObj.id ? (
                  <IconButton size="small" onClick={() => saveNewName(fileObj)}>
                    <Check fontSize="small" />
                  </IconButton>
                ) : (
                  <>
                    {isEditable && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingId(fileObj.id);
                          setEditValue(nameOnly);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => onDelete(fileObj)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
